import { z } from 'zod';
import { clientPlatformSchema } from '../enums';
import { apiErrorSchema } from '../errors';
import { isoDateTimeSchema, uuidSchema } from '../contracts/common';
import { presenceDtoSchema } from './payloads';

/**
 * The realtime wire protocol, version 1 (PLAN §5 "Connection lifecycle", "Envelope and
 * versioning", "Client → server ops"; D-014, D-075, D-077). JSON text frames only.
 *
 * Envelope: `{ t, d, seq?, cid?, rid?, eid? }` — `t` the event or op type, `d` the payload,
 * `seq` only on `message.created` (the conversation seq, D-015), `cid` on conversation-scoped
 * events, `rid` pairs a client op with its reply, `eid` the Redis Stream id of buffered events
 * (the resume cursor, D-075). Clients ignore unknown `t` and unknown fields (additive changes
 * within a protocol version).
 *
 * The socket carries no durable mutations (D-077): sending, editing, reacting and marking read
 * are REST. Clients send only the ops below.
 */

export const PROTOCOL_VERSION = 1;
/** Versions the gateway accepts in `?v=` (current and previous for 90 days after a bump). */
export const SUPPORTED_PROTOCOL_VERSIONS: readonly number[] = [1];
/** The WebSocket path; the query string carries only `v` (never a token, D-014). */
export const REALTIME_PATH = '/ws';

/** Timers and limits (the gateway may send different values in `hello.limits`). */
export const REALTIME_LIMITS = Object.freeze({
  authTimeoutMs: 10_000,
  /** Client app-level `ping` interval; the server's protocol pings use the same period. */
  heartbeatIntervalMs: 25_000,
  /** Client: no `pong` within this after a ping → reconnect. */
  pongTimeoutMs: 10_000,
  /** Client: no inbound frame of any kind for this long → reconnect. */
  clientSilenceTimeoutMs: 60_000,
  /** Client wake-ups (visibility, `online`): ping with this timeout, reconnect at once on failure. */
  wakePingTimeoutMs: 5_000,
  /** Server: no client frame for this long → close 4010. */
  serverSilenceTimeoutMs: 90_000,
  /** Refresh the access token this long before `tokenExpiresAt` and send `auth.refresh`. */
  tokenRefreshLeadMs: 60_000,
  /** Grace after `exp` before close 4003. */
  tokenGraceMs: 30_000,
  maxFrameBytes: 65_536,
  /** Client: send `typing.start` at most this often while typing. */
  typingThrottleMs: 3_000,
  /** A typing entry expires this long after its last refresh. */
  typingTtlMs: 6_000,
  presenceHeartbeatMs: 30_000,
  /** Default `presence.idleMinutes` (10). */
  idleAfterMs: 600_000,
  maxViewedConversations: 5,
  maxPresenceUserIds: 500,
  maxResumeConversations: 200,
  /** Replay buffers keep 15 minutes; a cursor older than 890 s means a full resync. */
  replayWindowSec: 900,
  replayMaxAgeSec: 890,
  /** Client: wait this long before fetching a seq gap (two API nodes can publish out of order). */
  gapTimerMs: 2_000,
  /** Client: a lag above this many messages resets the conversation to its latest page. */
  gapResetThreshold: 300,
  /** Client: after this long disconnected, poll the open conversation over REST … */
  degradedPollAfterMs: 30_000,
  /** … every this often. */
  degradedPollIntervalMs: 15_000,
  /** Client: debounce mark-read calls while scrolling. */
  markReadDebounceMs: 1_500,
  /** Phase 3: `notification.ack` frames per second per socket (D-501). */
  notificationAckPerSecond: 20,
});

// ================================================================ envelope

/** Per-socket request id: a counter, at most 16 characters. */
export const ridSchema = z.string().min(1).max(16);

/** Every server → client frame. `d` is validated per `t` by the event registry. */
export const serverEnvelopeSchema = z.object({
  t: z.string().min(1).max(64),
  d: z.record(z.string(), z.unknown()),
  seq: z.number().int().positive().optional(),
  cid: uuidSchema.optional(),
  rid: ridSchema.optional(),
  eid: z.string().optional(),
});
export type ServerEnvelope = z.infer<typeof serverEnvelopeSchema>;

// ================================================================ client → server ops

const presenceHeartbeatData = z.strictObject({
  /** `idle` after `limits.idleAfterMs` without input (web), OS idle time (desktop). */
  state: z.enum(['active', 'idle']),
  focused: z.boolean(),
  lastInputAt: isoDateTimeSchema,
});
export type PresenceHeartbeat = z.infer<typeof presenceHeartbeatData>;

export const resumeStateSchema = z.strictObject({
  epoch: z.string().min(1).max(32),
  cursor: z.string().min(1).max(40),
  /** Highest contiguous seq per cached conversation. */
  conversations: z
    .array(z.strictObject({ cid: uuidSchema, seq: z.number().int().nonnegative() }))
    .max(REALTIME_LIMITS.maxResumeConversations),
});
export type ResumeState = z.infer<typeof resumeStateSchema>;

export const authOpSchema = z.strictObject({
  t: z.literal('auth'),
  rid: ridSchema.optional(),
  d: z.strictObject({
    token: z.string().min(1).max(4096),
    client: z.strictObject({
      platform: clientPlatformSchema,
      build: z.string().min(1).max(64),
      /** Stable per install (web: localStorage). Presence is tracked per device. */
      deviceId: uuidSchema,
    }),
    /** Omitted on a first-ever connect. */
    resume: resumeStateSchema.optional(),
    /** Restores the view and presence interest the client had before a reconnect. */
    subscriptions: z
      .strictObject({
        conversations: z.array(uuidSchema).max(REALTIME_LIMITS.maxViewedConversations).default([]),
        presenceUserIds: z.array(uuidSchema).max(REALTIME_LIMITS.maxPresenceUserIds).default([]),
      })
      .optional(),
    presence: presenceHeartbeatData.optional(),
  }),
});
export type AuthOp = z.infer<typeof authOpSchema>;

export const authRefreshOpSchema = z.strictObject({
  t: z.literal('auth.refresh'),
  rid: ridSchema.optional(),
  d: z.strictObject({ token: z.string().min(1).max(4096) }),
});

export const pingOpSchema = z.strictObject({
  t: z.literal('ping'),
  rid: ridSchema.optional(),
  d: z.strictObject({}),
});

const typingData = z.strictObject({
  cid: uuidSchema,
  threadRootId: uuidSchema.nullable().optional(),
});

export const typingStartOpSchema = z.strictObject({
  t: z.literal('typing.start'),
  rid: ridSchema.optional(),
  d: typingData,
});

export const typingStopOpSchema = z.strictObject({
  t: z.literal('typing.stop'),
  rid: ridSchema.optional(),
  d: typingData,
});

export const presenceHeartbeatOpSchema = z.strictObject({
  t: z.literal('presence.heartbeat'),
  rid: ridSchema.optional(),
  d: presenceHeartbeatData,
});

/** Replaces the socket's presence-interest set; the reply is `presence.snapshot` for new ids. */
export const presenceSubscribeOpSchema = z.strictObject({
  t: z.literal('presence.subscribe'),
  rid: ridSchema.optional(),
  d: z.strictObject({ userIds: z.array(uuidSchema).max(REALTIME_LIMITS.maxPresenceUserIds) }),
});

/**
 * Marks a conversation as viewed (typing events). Members need no ticket; a public-channel
 * preview needs one from `POST /api/v1/realtime/tickets` (D-077).
 */
export const conversationSubscribeOpSchema = z.strictObject({
  t: z.literal('conversation.subscribe'),
  rid: ridSchema.optional(),
  d: z.strictObject({ cid: uuidSchema, ticket: z.string().max(1024).optional() }),
});

export const conversationUnsubscribeOpSchema = z.strictObject({
  t: z.literal('conversation.unsubscribe'),
  rid: ridSchema.optional(),
  d: z.strictObject({ cid: uuidSchema }),
});

/**
 * Phase 3 (D-501): the foreground mobile app showed the in-app banner for a live
 * `notification.created` (never a replayed one), so the fallback push is not needed. The gateway
 * writes a 60 s ack key the fallback job checks; no durable state changes (D-077). 20 per second.
 */
export const notificationAckOpSchema = z.strictObject({
  t: z.literal('notification.ack'),
  rid: ridSchema.optional(),
  d: z.strictObject({ notificationId: uuidSchema }),
});
export type NotificationAckOp = z.infer<typeof notificationAckOpSchema>;

export const clientFrameSchema = z.discriminatedUnion('t', [
  authOpSchema,
  authRefreshOpSchema,
  pingOpSchema,
  typingStartOpSchema,
  typingStopOpSchema,
  presenceHeartbeatOpSchema,
  presenceSubscribeOpSchema,
  conversationSubscribeOpSchema,
  conversationUnsubscribeOpSchema,
  notificationAckOpSchema,
]);
export type ClientFrame = z.infer<typeof clientFrameSchema>;
export type ClientOpType = ClientFrame['t'];
export const CLIENT_OP_TYPES = clientFrameSchema.options.map((o) => o.shape.t.value);

// ================================================================ connection and control frames

export const resyncLevelSchema = z.enum(['none', 'partial', 'full']);
export type ResyncLevel = z.infer<typeof resyncLevelSchema>;

/** `hello` — the answer to `auth` (PLAN §5 "Server hello"). */
export const helloDataSchema = z.object({
  protocol: z.number().int(),
  /** One per socket (UUIDv7); clients send it as `X-Hub-Connection-Id` on REST calls. */
  connectionId: uuidSchema,
  authSessionId: uuidSchema,
  userId: uuidSchema,
  workspaceId: uuidSchema,
  serverTime: isoDateTimeSchema,
  heartbeatIntervalMs: z.number().int().positive(),
  tokenExpiresAt: isoDateTimeSchema,
  resume: z.object({
    supported: z.boolean(),
    windowSec: z.number().int().positive(),
    /** First 8 characters of the Redis `run_id`; changes on every Redis restart (D-075). */
    epoch: z.string(),
    /** `fresh` (no cursor), `replay` (missed events follow) or `full_resync`. */
    mode: z.enum(['fresh', 'replay', 'full_resync']),
  }),
  limits: z.object({
    maxFrameBytes: z.number().int(),
    typingThrottleMs: z.number().int(),
    typingTtlMs: z.number().int(),
    presenceHeartbeatMs: z.number().int(),
    idleAfterMs: z.number().int(),
    maxViewedConversations: z.number().int(),
    maxPresenceUserIds: z.number().int(),
  }),
  client: z.object({ minBuild: z.string().nullable(), latestBuild: z.string().nullable() }),
  serverBuild: z.string(),
});
export type HelloData = z.infer<typeof helloDataSchema>;

/** `pong` — reply to `ping`; `cursor` is the node's watermark, so idle sockets keep a fresh cursor. */
export const pongDataSchema = z.object({ serverTime: isoDateTimeSchema, cursor: z.string() });
export type PongData = z.infer<typeof pongDataSchema>;

/** `ack` — success reply to an op that expects one (`auth.refresh`, `conversation.subscribe`, …). */
export const ackDataSchema = z.record(z.string(), z.unknown());

/** `conversation.subscribe` ack. */
export const conversationSubscribeAckSchema = z.object({
  typing: z.array(
    z.object({
      userId: uuidSchema,
      threadRootId: uuidSchema.nullable(),
      expiresAt: isoDateTimeSchema,
    }),
  ),
  preview: z.boolean(),
});
export type ConversationSubscribeAck = z.infer<typeof conversationSubscribeAckSchema>;

/** `error` — same shape as REST errors (D-011); never closes the socket by itself. */
export const errorFrameDataSchema = apiErrorSchema;
export type ErrorFrameData = z.infer<typeof errorFrameDataSchema>;

/** `sync.completed` — end of the resume replay (PLAN §5 "Resume protocol"). */
export const syncCompletedDataSchema = z.object({
  resync: resyncLevelSchema,
  replayed: z.number().int().nonnegative(),
  cursor: z.string(),
  gaps: z.object({ conversations: z.array(uuidSchema) }),
  /** For each cid the client sent whose server state differs. */
  conversations: z.array(
    z.object({ cid: uuidSchema, lastSeq: z.number().int(), lastReadSeq: z.number().int() }),
  ),
});
export type SyncCompletedData = z.infer<typeof syncCompletedDataSchema>;

export const authExpiringDataSchema = z.object({ expiresAt: isoDateTimeSchema });
export const presenceSnapshotDataSchema = z.object({ users: z.array(presenceDtoSchema) });
export const systemReconnectDataSchema = z.object({
  afterMs: z.number().int().nonnegative(),
  reason: z.enum(['draining', 'rebalance']),
});

/** Frames the node sends to one socket (not through Redis). */
export const CONTROL_FRAME_TYPES = [
  'hello',
  'pong',
  'ack',
  'error',
  'sync.completed',
  'auth.expiring',
  'presence.snapshot',
  'system.reconnect',
] as const;
export type ControlFrameType = (typeof CONTROL_FRAME_TYPES)[number];

export const controlFrameSchemas = {
  hello: helloDataSchema,
  pong: pongDataSchema,
  ack: ackDataSchema,
  error: errorFrameDataSchema,
  'sync.completed': syncCompletedDataSchema,
  'auth.expiring': authExpiringDataSchema,
  'presence.snapshot': presenceSnapshotDataSchema,
  'system.reconnect': systemReconnectDataSchema,
} as const satisfies Record<ControlFrameType, z.ZodType>;
